
import React, { useState, useEffect } from 'react';
import Header from '../Components/Header'
import MapVisual from '../Components/MapVisual';
import UtilityMapVisual from '../Components/UtilityMapVisual';
import { clearUtilityDataCache } from '../lib/utilityData';
import { canUseUtilityView } from '../lib/utilityView';
import WeatherAnalysis from '../Components/WeatherAnalysis';
import '../App.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import Prediction from '../Components/Prediction';
import apiClient from '../lib/api-client';
import LoadingOverlay from '../Components/LoadingOverlay';
import WeeklyMapVisual from '../Components/WeeklyMapVisual';
import TopStatsPanel from '../Components/TopStatsPanel';
import TopNews from '../Components/TopNews';
import FutureWeather from '../Components/FutureWeather';
import SignInPromptModal from '../Components/SignInPromptModal';
import ProUpgradeModal from '../Components/ProUpgradeModal';
import { useAuth } from '../AuthContext';
import { DateTime } from 'luxon';

const COUNT_BUCKET_TO_COUNT = {
  lt_10k: 5000,
  '10k_50k': 30000,
  '50k_100k': 75000,
  '100k_500k': 300000,
  gte_500k: 500000
};

const bucketToRepresentativeCount = (bucket) => {
  if (typeof bucket === 'number') return bucket;
  if (!bucket) return 0;
  if (COUNT_BUCKET_TO_COUNT[bucket] !== undefined) return COUNT_BUCKET_TO_COUNT[bucket];
  const normalized = String(bucket).replaceAll('-', '_');
  return COUNT_BUCKET_TO_COUNT[normalized] ?? 0;
};

const parseEstimateGuest = (estimate) => {
  if (!estimate) return { count: 0, percentOut: 0 };

  return {
    count: estimate.count_bucket
      ? bucketToRepresentativeCount(estimate.count_bucket)
      : (estimate.value ?? 0),
    percentOut: estimate.percent_out ?? 0
  };
};

const parseEstimateSignup = (estimate) => {
  if (!estimate) return { count: 0, percentOut: 0 };

  return {
    count: estimate.value ?? 0, // STRICT: always value
    percentOut: estimate.percent_out ?? 0
  };
};

const normalizePredictionResponse = (response, parser) => {
  const normalized = {};
  const predictionsByState = response?.predictions_by_state || {};

  Object.entries(predictionsByState).forEach(([stateFipsKey, entries]) => {
    if (!Array.isArray(entries)) return;
    const stateFips = String(stateFipsKey).padStart(2, '0');
    normalized[stateFips] = entries.map((entry) => {
      const customersMin = parser(entry?.customers?.min);
      const customersMean = parser(entry?.customers?.mean);
      const customersMax = parser(entry?.customers?.max);
      const outageMin = parser(entry?.outage?.min);
      const outageMean = parser(entry?.outage?.mean);
      const outageMax = parser(entry?.outage?.max);

      return {
        date: entry.date,
        customers: {
          min: customersMin.count,
          mean: customersMean.count,
          max: customersMax.count,
          min_percent_out: customersMin.percentOut,
          mean_percent_out: customersMean.percentOut,
          max_percent_out: customersMax.percentOut,
          weather_severity: entry?.customers?.weather_severity ?? 0
        },
        outage: {
          min: outageMin.count,
          mean: outageMean.count,
          max: outageMax.count,
          min_percent_out: outageMin.percentOut,
          mean_percent_out: outageMean.percentOut,
          max_percent_out: outageMax.percentOut,
          weather_severity: entry?.outage?.weather_severity ?? 0
        }
      };
    });
  });

  return normalized;
};

function Home() {
  const { isAuthenticated, isGuest, userTier, checkingAuth } = useAuth(); // 'guest' | 'signup' | 'pro'
  const [currentMetric, setCurrentMetric] = useState('mean');
  const [currentDataType, setCurrentDataType] = useState('customers');
  const [currentChartType, setCurrentChartType] = useState('wind_speed');
  const [currentRegion, setCurrentRegion] = useState(null);
  const [predictionData, setPredictionData] = useState(null);
  const [countyData, setCountyData] = useState(null);
  const [countiesList, setCountiesList] = useState(null);
  const [isToday, setIsToday] = useState(true);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isPredictionLoading, setIsPredictionLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Loading application data...');
  const selectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  // Initialize to 'US' to show full map
  const [selectedStateId, setSelectedStateId] = useState('US');
  const [statesList, setStatesList] = useState([]);
  const [predictionType, setPredictionType] = useState('daily');
  const [stateMapMode, setStateMapMode] = useState('county');
  const [isFuture, setIsFuture] = useState(true);
  const [selectedDay, setSelectedDay] = useState(new Date().toISOString().split('T')[0]);
  const [activeMainTab, setActiveMainTab] = useState('home');
  const [activeDetailTab, setActiveDetailTab] = useState('weekly');
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [showProModal, setShowProModal] = useState(false);
  // Ref flag: only true when signup state drill-down was explicitly approved by usage check.
  // Prevents the signup state useEffect from calling makeFuturePrediction without a prior check.
  const signupDrillApproved = React.useRef(false);
  const [freeTopStatesCustomersOut, setFreeTopStatesCustomersOut] = useState([]);
  const [signupTopStatesPercent, setSignupTopStatesPercent] = useState([]);
  const [signupTopCountiesOut, setSignupTopCountiesOut] = useState([]);
  const [signupTopCountiesPercent, setSignupTopCountiesPercent] = useState([]);

  const isUtilityViewAvailable = canUseUtilityView({
    isAuthenticated,
    predictionType,
    stateId: selectedStateId,
    userTier
  });

  useEffect(() => {
    if (!isUtilityViewAvailable && stateMapMode !== 'county') {
      setStateMapMode('county');
    }
  }, [isUtilityViewAvailable, stateMapMode]);

  const changeStateMapMode = (mode) => {
    setStateMapMode(mode);
    setCurrentRegion(null);
    setCountyData(null);
  };


  // Fetch initial data on page load
  useEffect(() => {
    if (checkingAuth) return;

    const fetchInitialData = async () => {
      try {
        setIsLoading(true);
        setLoadingMessage('Fetching US Data...');

      // if guest, give empty states and county list; else load from backend
      if (userTier === 'guest') {
          setStatesList([]);
          setCountiesList([]);
        } else {
        const states = await apiClient.listStates();
        setStatesList(states);
        const counties = await apiClient.listCounties();
        setCountiesList(counties);
        }

        // 3. Init Dates
        const today = DateTime.now().toISODate();
        const nextWeek = DateTime.now().plus({ days: 6 }).toISODate();

        setStartDate(today);
        setEndDate(nextWeek);
        setSelectedDay(today);

      } catch (error) {
        console.error('Error fetching initial data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialData();
  }, [checkingAuth, userTier]);

  const [error, setError] = useState(null);

  // Cache for US Aggregated Data
  const [usDataCache, setUsDataCache] = useState(null);
  const [usTopStatesCustomersOutCache, setUsTopStatesCustomersOutCache] = useState(null);
  const [usTopStatesPercentCache, setUsTopStatesPercentCache] = useState(null);
  const [usTopCountiesOutCache, setUsTopCountiesOutCache] = useState(null);
  const [usTopCountiesPercentCache, setUsTopCountiesPercentCache] = useState(null);

  // Clear cache when auth state changes to ensure we fetch fresh data for the new role
  useEffect(() => {
    setUsDataCache(null);
    setUsTopStatesCustomersOutCache(null);
    setUsTopStatesPercentCache(null);
    setUsTopCountiesOutCache(null);
    setUsTopCountiesPercentCache(null);
    setPredictionData(null);
    setSignupTopStatesPercent([]);
    setSignupTopCountiesOut([]);
    setSignupTopCountiesPercent([]);
    clearUtilityDataCache();
  }, [userTier]);

  // Fetch Prediction Data for GUEST only US view
 useEffect(() => {
  if (checkingAuth || userTier !== 'guest') return;
  if (selectedStateId!=='US') return;

  const fetchUSGuestPrediction = async () => {
    // cache hit
    if (usDataCache) {
      setPredictionData(usDataCache);
      if (usTopStatesCustomersOutCache) {
        setFreeTopStatesCustomersOut(usTopStatesCustomersOutCache);
      }
      return;
    }

    setError(null);

    const today = DateTime.now().toISODate();
    const nextWeek = DateTime.now().plus({ days: 6 }).toISODate();

    setIsPredictionLoading(true);
    setLoadingMessage('Loading US Data...');

    try {
      const noLoginResponse = await apiClient.getUsPredictionNoLogin(
        today,
        nextWeek,
        selectedTimezone
      );

      const normalizedUsData = normalizePredictionResponse(noLoginResponse, parseEstimateGuest);
      const freeTopStates = Array.isArray(noLoginResponse?.top_10_states_customers_out)
        ? noLoginResponse.top_10_states_customers_out
        : [];

      setPredictionData(normalizedUsData);
      setUsDataCache(normalizedUsData);
      setFreeTopStatesCustomersOut(freeTopStates);
      setUsTopStatesCustomersOutCache(freeTopStates);

    } catch (e) {
      console.error("US guest prediction failed", e);
      setError(`Fetch failed: ${e.message}`);
    } finally {
      setIsPredictionLoading(false);
    }
  };

  fetchUSGuestPrediction();
}, [checkingAuth, userTier, selectedStateId, selectedTimezone, usDataCache, usTopStatesCustomersOutCache]);

// signup version/free version — US view only
useEffect(() => {
  if (checkingAuth || userTier !== 'signup') return;
  if (selectedStateId !== 'US') return;

  const fetchUSSignupPrediction = async () => {
    // Cache hit — restore from cache without re-fetching
    if (usDataCache) {
      setPredictionData(usDataCache);
      if (usTopStatesCustomersOutCache) setFreeTopStatesCustomersOut(usTopStatesCustomersOutCache);
      if (usTopStatesPercentCache) setSignupTopStatesPercent(usTopStatesPercentCache);
      if (usTopCountiesOutCache) setSignupTopCountiesOut(usTopCountiesOutCache);
      if (usTopCountiesPercentCache) setSignupTopCountiesPercent(usTopCountiesPercentCache);
      return;
    }

    setError(null);
    setIsPredictionLoading(true);
    setLoadingMessage('Loading US Data...');

    const today = DateTime.now().toISODate();
    const nextWeek = DateTime.now().plus({ days: 6 }).toISODate();

    try {
      const loginUSResponse = await apiClient.getUsPredictionLogin(
        today,
        nextWeek,
        selectedTimezone
      );

      const normalizedUsLoginData = normalizePredictionResponse(loginUSResponse, parseEstimateSignup);
      const signupTopStates = Array.isArray(loginUSResponse?.top_10_states_customers_out)
        ? loginUSResponse.top_10_states_customers_out : [];
      const signupTopStatesPercentArr = Array.isArray(loginUSResponse?.top_10_states_percent_out)
        ? loginUSResponse.top_10_states_percent_out : [];
      const signupTopCountiesOutArr = Array.isArray(loginUSResponse?.top_10_counties_customers_out)
        ? loginUSResponse.top_10_counties_customers_out : [];
      const signupTopCountiesPercentArr = Array.isArray(loginUSResponse?.top_10_counties_percent_out)
        ? loginUSResponse.top_10_counties_percent_out : [];

      setPredictionData(normalizedUsLoginData);
      setUsDataCache(normalizedUsLoginData);
      setFreeTopStatesCustomersOut(signupTopStates);
      setUsTopStatesCustomersOutCache(signupTopStates);
      setSignupTopStatesPercent(signupTopStatesPercentArr);
      setUsTopStatesPercentCache(signupTopStatesPercentArr);
      setSignupTopCountiesOut(signupTopCountiesOutArr);
      setUsTopCountiesOutCache(signupTopCountiesOutArr);
      setSignupTopCountiesPercent(signupTopCountiesPercentArr);
      setUsTopCountiesPercentCache(signupTopCountiesPercentArr);

    } catch (e) {
      console.error('US signup prediction failed', e);
      setError(e.message);
    } finally {
      setIsPredictionLoading(false);
    }
  };

  fetchUSSignupPrediction();
  // Only re-run when tier, state selection, timezone, or the primary cache changes.
  // Individual top-stats caches are read inside the function — not deps — to avoid
  // re-triggering the effect every time a cache entry is cleared.
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [checkingAuth, userTier, selectedStateId, selectedTimezone, usDataCache]);


// SIGNUP — state drill-down view using makeFuturePrediction (same as pro single-state flow)
useEffect(() => {
  if (checkingAuth || userTier !== 'signup') return;
  if (!selectedStateId || selectedStateId === 'US') return;
  if (!statesList || statesList.length === 0) return;
  // Only proceed if the usage check in handleCountyClick explicitly approved this drill-down
  if (!signupDrillApproved.current) return;
  // Consume the approval — reset so subsequent re-runs don't bypass the check
  signupDrillApproved.current = false;

  const fetchSignupStatePrediction = async () => {
    setError(null);
    setIsPredictionLoading(true);
    setLoadingMessage('Loading state data...');

    const today = DateTime.now().toISODate();
    const nextWeek = DateTime.now().plus({ days: 6 }).toISODate();

    try {
      let backendId = selectedStateId;
      const state = statesList.find(
        s => parseInt(s.fips) === parseInt(selectedStateId)
      );
      if (state) backendId = state.id;

      const prediction = await apiClient.makeFuturePrediction(
        backendId,
        today,
        nextWeek,
        selectedTimezone
      );
      setPredictionData(prediction);
      // Notify Header to refresh the usage counter
      window.dispatchEvent(new CustomEvent('usageUpdated'));
    } catch (e) {
      console.error('Signup state prediction failed', e);
      setError(`Fetch failed: ${e.message}`);
    } finally {
      setIsPredictionLoading(false);
    }
  };

  fetchSignupStatePrediction();
}, [checkingAuth, userTier, selectedStateId, statesList, selectedTimezone]);

// PRO — US view using getUsPredictionLogin (same endpoint as signup)
useEffect(() => {
  if (checkingAuth || userTier !== 'pro') return;
  if (selectedStateId !== 'US') return;

  const fetchUSProPrediction = async () => {
    if (usDataCache) {
      setPredictionData(usDataCache);
      if (usTopStatesCustomersOutCache) setFreeTopStatesCustomersOut(usTopStatesCustomersOutCache);
      if (usTopStatesPercentCache) setSignupTopStatesPercent(usTopStatesPercentCache);
      if (usTopCountiesOutCache) setSignupTopCountiesOut(usTopCountiesOutCache);
      if (usTopCountiesPercentCache) setSignupTopCountiesPercent(usTopCountiesPercentCache);
      return;
    }

    setError(null);
    setIsPredictionLoading(true);
    setLoadingMessage('Loading US Data...');

    const today = DateTime.now().toISODate();
    const nextWeek = DateTime.now().plus({ days: 6 }).toISODate();

    try {
      const loginUSResponse = await apiClient.getUsPredictionLogin(
        today,
        nextWeek,
        selectedTimezone
      );

      const normalizedData = normalizePredictionResponse(loginUSResponse, parseEstimateSignup);
      const topStates = Array.isArray(loginUSResponse?.top_10_states_customers_out)
        ? loginUSResponse.top_10_states_customers_out : [];
      const topStatesPercentArr = Array.isArray(loginUSResponse?.top_10_states_percent_out)
        ? loginUSResponse.top_10_states_percent_out : [];
      const topCountiesOutArr = Array.isArray(loginUSResponse?.top_10_counties_customers_out)
        ? loginUSResponse.top_10_counties_customers_out : [];
      const topCountiesPercentArr = Array.isArray(loginUSResponse?.top_10_counties_percent_out)
        ? loginUSResponse.top_10_counties_percent_out : [];

      setPredictionData(normalizedData);
      setUsDataCache(normalizedData);
      setFreeTopStatesCustomersOut(topStates);
      setUsTopStatesCustomersOutCache(topStates);
      setSignupTopStatesPercent(topStatesPercentArr);
      setUsTopStatesPercentCache(topStatesPercentArr);
      setSignupTopCountiesOut(topCountiesOutArr);
      setUsTopCountiesOutCache(topCountiesOutArr);
      setSignupTopCountiesPercent(topCountiesPercentArr);
      setUsTopCountiesPercentCache(topCountiesPercentArr);

    } catch (e) {
      console.error('Pro US prediction failed', e);
      setError(e.message);
    } finally {
      setIsPredictionLoading(false);
    }
  };

  fetchUSProPrediction();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [checkingAuth, userTier, selectedStateId, selectedTimezone, usDataCache]);

// PRO — state drill-down using makeFuturePrediction
useEffect(() => {
  if (checkingAuth || userTier !== 'pro') return;
  if (!selectedStateId || selectedStateId === 'US') return;
  if (!statesList || statesList.length === 0) return;

  const fetchProStatePrediction = async () => {
    setError(null);
    setIsPredictionLoading(true);
    setLoadingMessage('Loading state data...');

    const today = DateTime.now().toISODate();
    const nextWeek = DateTime.now().plus({ days: 6 }).toISODate();

    try {
      let backendId = selectedStateId;
      const state = statesList.find(
        s => parseInt(s.fips) === parseInt(selectedStateId)
      );
      if (state) backendId = state.id;

      const prediction = await apiClient.makeFuturePrediction(
        backendId,
        today,
        nextWeek,
        selectedTimezone
      );
      setPredictionData(prediction);
    } catch (e) {
      console.error('Pro state prediction failed', e);
      setError(`Fetch failed: ${e.message}`);
    } finally {
      setIsPredictionLoading(false);
    }
  };

  fetchProStatePrediction();
}, [checkingAuth, userTier, selectedStateId, statesList, selectedTimezone]);



  const handleCountyClick = async (data) => {
    // If guest tries to drill into a state, show the sign-in prompt instead
    if (isGuest && data.type === 'STATE') {
      setShowSignInModal(true);
      return;
    }

    // Check if we are drilling down to a state
    if (data.type === 'STATE') {
      // Signup tier: check daily usage limit before allowing state drill-down
      if (userTier === 'signup') {
        try {
          const usage = await apiClient.getUsageDetails();
          if (usage.daily_request_count >= usage.daily_predictions_limit) {
            setShowProModal(true);
            return; // Keep user on US view — do NOT set selectedStateId
          }
        } catch (e) {
          console.error('Usage check failed', e);
          // On network error, block the drill-down to be safe
          return;
        }
        // Usage check passed — mark drill-down as approved before setting state
        signupDrillApproved.current = true;
      }

      if (predictionType === 'weekly') {
        setPredictionType('daily');
        setSelectedDay(data.selectedDay || selectedDay);
      }

      setSelectedStateId(data.id);
      setStateMapMode('county');
      setCurrentRegion(null);
      setCountyData(null);
      return;
    }

    // Otherwise standard county selection for details
    const county = countiesList?.find(c => c.fips.toString() === data.id.toString());
    if (county) {
      data.county_id = county.id;
      setCountyData(data);
      setCurrentRegion(data);
    } else {
      console.warn("Could not find county metadata for clicked region", data);
      setCountyData(data);
      setCurrentRegion(data);
    }
  };

  return (


    <>
      {(isLoading || isPredictionLoading) && <LoadingOverlay message={loadingMessage} />}

      <div className='container-flex dashboard-shell'>
        <div className="row g-4 mx-md-4 mt-1 dashboard-row">
          <Header />

          {/* Top News Banner */}
          <div className="col-12">
            <TopNews selectedStateId={selectedStateId} />
          </div>

          {/* Navigation Section */}
          <div className="col-12 d-flex justify-content-center gap-4 my-2 align-items-center date-nav-row">

            {/* Main View Toggle: Outages / Weather */}
            <div className="btn-group shadow-sm main-view-toggle" role="group">
              <button
                type="button"
                className={`btn ${activeMainTab === 'home' ? 'btn-secondary' : 'btn-outline-secondary'} px-4 py-2`}
                onClick={() => setActiveMainTab('home')}
              >
                Outages
              </button>
              <button
                type="button"
                className={`btn ${activeMainTab === 'weather' ? 'btn-secondary' : 'btn-outline-secondary'} px-4 py-2`}
                onClick={() => setActiveMainTab('weather')}
              >
                Weather
              </button>
            </div>

            {/* Date Tab Selector */}
            <div className="date-selector-scroll" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', whiteSpace: 'nowrap' }}>
              <div className="btn-group shadow-sm" role="group">
                {Array.from({ length: 7 }).map((_, index) => {
                  const date = DateTime.now().plus({ days: index });
                  const dateStr = date.toISODate();
                  const displayStr = date.toFormat('EEE dd'); // e.g., "Mon 15"
                  const isActive = predictionType === 'daily' && selectedDay === dateStr;

                return (
                  <button
                    key={dateStr}
                    type="button"
                    className={`btn ${isActive ? 'btn-primary' : 'btn-outline-primary'} px-4 py-2`}
                    onClick={() => {
                      if (predictionType === 'weekly') {
                        setCurrentRegion(null);
                        setCountyData(null);
                      }
                      setPredictionType('daily');
                      setSelectedDay(dateStr);
                    }}
                  >
                    {displayStr}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

          {selectedStateId !== 'US' && activeMainTab === 'home' && (
            <div className={`d-flex justify-content-between align-items-center flex-wrap mb-2 back-btn-row ${isUtilityViewAvailable ? 'has-state-map-toggle' : ''}`}>
              <button className="btn btn-secondary back-to-us-btn" onClick={() => {
                signupDrillApproved.current = false;
                setSelectedStateId('US');
                setStateMapMode('county');
                setCurrentRegion(null);
                setCountyData(null);
              }}>
                <i className="fas fa-arrow-left me-2"></i> Back to US Map
              </button>

              {activeMainTab === 'home' && isUtilityViewAvailable && (
                <div className="btn-group state-map-view-toggle" role="group" aria-label="State map detail">
                  <button
                    type="button"
                    className={`btn ${stateMapMode === 'county' ? 'btn-secondary active' : 'btn-outline-secondary'}`}
                    onClick={() => changeStateMapMode('county')}
                    aria-pressed={stateMapMode === 'county'}
                  >
                    <i className="fas fa-map-marked-alt me-1" aria-hidden="true"></i>
                    County view
                  </button>
                  <button
                    type="button"
                    className={`btn ${stateMapMode === 'utility' ? 'btn-secondary active' : 'btn-outline-secondary'}`}
                    onClick={() => changeStateMapMode('utility')}
                    aria-pressed={stateMapMode === 'utility'}
                  >
                    <i className="fas fa-bolt me-1" aria-hidden="true"></i>
                    Utility view
                  </button>
                </div>
              )}
            </div>
          )}

          {/* HOME TAB CONTENT */}
          {activeMainTab === 'home' && (
            <>
              {/* The national and county maps retain their existing behavior;
                  utility view replaces the map surface only when selected. */}
              {stateMapMode === 'utility' && isUtilityViewAvailable ? (
                <UtilityMapVisual
                  stateId={selectedStateId}
                  selectedDay={selectedDay}
                  currentMetric={currentMetric}
                  setCurrentMetric={setCurrentMetric}
                  startDate={startDate}
                  endDate={endDate}
                  timezone={selectedTimezone}
                />
              ) : (
                <MapVisual
                  currentMetric={currentMetric}
                  currentDataType={currentDataType}
                  currentRegion={currentRegion}
                  setCurrentRegion={setCurrentRegion}
                  setCurrentMetric={setCurrentMetric}
                  setCurrentDataType={setCurrentDataType}
                  setPredictionType={setPredictionType}
                  predictionData={predictionData || {}}
                  dataLevel={userTier === 'pro' ? 'county' : 'state'}
                  onCountyClick={handleCountyClick}
                  countiesList={countiesList || []}
                  isToday={isToday}
                  startDate={startDate}
                  endDate={endDate}
                  timezone={selectedTimezone}
                  stateId={selectedStateId}
                  selectedDay={selectedDay}
                  predictionType={predictionType}
                  isAuthenticated={isAuthenticated}
                  freeTopStatesCustomersOut={freeTopStatesCustomersOut}
                  statesList={statesList || []}
                />
              )}

              {/* Weekly Map, Chart, and Weather Analysis Views */}
              {selectedStateId !== 'US' && stateMapMode === 'county' && (
                <div className="mb-4 mt-2 dashboard-detail-section">
                  <div className="d-flex justify-content-center mb-3 detail-tab-row">
                    <div className="btn-group shadow-sm detail-tab-group">
                      <button
                        className={`btn ${activeDetailTab === 'weekly' ? 'btn-secondary' : 'btn-outline-secondary'} px-4 py-2`}
                        onClick={() => setActiveDetailTab('weekly')}
                      >
                        Weekly Trends
                      </button>
                      <button
                        className={`btn ${activeDetailTab === 'hourly' ? 'btn-secondary' : 'btn-outline-secondary'} px-4 py-2`}
                        onClick={() => setActiveDetailTab('hourly')}
                      >
                        Hourly Weather
                      </button>
                    </div>
                  </div>

                  {activeDetailTab === 'weekly' && (
                    <WeeklyMapVisual
                      currentRegion={currentRegion}
                      setCurrentRegion={setCurrentRegion}
                      predictionData={predictionData}
                      onCountyClick={handleCountyClick}
                      stateId={selectedStateId}
                    />
                  )}

                  {activeDetailTab === 'hourly' && (
                    <WeatherAnalysis
                      currentRegion={currentRegion}
                      setCurrentChartType={setCurrentChartType}
                      currentChartType={currentChartType}
                      countyData={countyData}
                      isToday={isToday}
                      startDate={startDate}
                      endDate={endDate}
                      timezone={selectedTimezone}
                    />
                  )}
                </div>
              )}

              {/* 
                  TopStatsPanel should ALWAYS show US-wide statistics.
                  If we are in 'US' view, predictionData IS the US data.
                  If we are in 'State' view, predictionData is just that state, so we fallback to usDataCache.
              */}
              {/* TopStatsPanel: hidden for guests */}
              {!isGuest && (
                <TopStatsPanel
                  predictionData={(selectedStateId === 'US' && predictionData) ? predictionData : (usDataCache || {})}
                  selectedDay={selectedDay}
                  countiesList={countiesList || []}
                  currentMetric={currentMetric}
                  signupTopStatesPercent={signupTopStatesPercent}
                  signupTopCountiesOut={signupTopCountiesOut}
                  signupTopCountiesPercent={signupTopCountiesPercent}
                />
              )}
            </>
          )}

          {/* WEATHER TAB CONTENT */}
          {activeMainTab === 'weather' && (
            <div className="col-12">
              <FutureWeather selectedDate={selectedDay} timezone={selectedTimezone} />
            </div>
          )}

        </div>
      </div>

      {/* Sign-in prompt modal for guest users */}
      <SignInPromptModal show={showSignInModal} onHide={() => setShowSignInModal(false)} />

      {/* Pro upgrade modal — shown when signup user hits daily limit */}
      <ProUpgradeModal show={showProModal} onHide={() => setShowProModal(false)} />
    </>
  );
}

export default Home;
